import IconButton from "@components/common/IconButton";
import { ReactComponent as ExitIcon } from "@images/exit_icon.svg";
import { ReactComponent as LoadingSpinner } from "@images/loading_spinner.svg";
import { CampaignType } from "@models/Enums";
import { PUBGrowCampaign, PUBGrowCampaignStatusResponse } from "@models/Models";
import { kAnalyticsConstants } from "@utils/constants/AnalyticsConstants";
import { recordEvent } from "@utils/managers/AnalyticsManager";
import { updateGrowCampaignStatus } from "@utils/managers/networking/NetworkManager";
import { generateToast } from "@utils/managers/ToastManager";
import { Dialog, Transition } from "@headlessui/react";
import { ExclamationTriangleIcon, InformationCircleIcon } from "@heroicons/react/24/solid";
import { FC, Fragment, useState } from "react";

interface CampaignStatusModalProps {
	selectedCampaign: PUBGrowCampaign;
	isShow: boolean;
	onClose: () => void;
	onStatusChanged: (status: PUBGrowCampaignStatusResponse) => void;
}

const CampaignStatusModal: FC<CampaignStatusModalProps> = ({ selectedCampaign, isShow, onClose, onStatusChanged }) => {
	const [isLoading, setLoading] = useState(false);
	const isTurningOn = !selectedCampaign.is_active;
	const isAffiliateEngaged = selectedCampaign.type === CampaignType.Engaged;
	const engagedWindow = selectedCampaign.engaged_window ?? 7;

	const closeModal = () => {
		if (!isLoading) {
			onClose();
		}
	};

	const changeCampaignStatus = () => {
		setLoading(true);

		updateGrowCampaignStatus(selectedCampaign.id, isTurningOn)
			.then((status) => {
				recordEvent(isTurningOn ? kAnalyticsConstants.Grow.campaignEnabled : kAnalyticsConstants.Grow.campaignDisabled, {
					campaign_id: selectedCampaign.id,
					campaign_type: selectedCampaign.type,
				});
				onStatusChanged(status);
				generateToast({
					status: "success",
					title: isTurningOn ? "Campaign turned on" : "Campaign turned off",
				});
				setLoading(false);
				onClose();
			})
			.catch(() => {
				setLoading(false);
				generateToast({
					status: "error",
					title: "Something went wrong",
					message: `We couldn't turn ${isTurningOn ? "on" : "off"} your campaign. Please try again later or contact us.`,
				});
			});
	};

	return (
		<Transition as={Fragment} show={isShow}>
			<Dialog onClose={closeModal} className="relative z-40">
				<Transition.Child as="div" enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0">
					<div className="fixed inset-0 bg-black bg-opacity-70" aria-hidden="true" />
				</Transition.Child>
				<div className="fixed inset-0 flex w-screen items-center justify-center p-4">
					<Transition.Child enter="ease-out duration-300" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100" leave="ease-in duration-200" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95">
						<Dialog.Panel className="w-full max-w-[480px] transform overflow-hidden rounded-2xl bg-secondary border border-primary-100 text-left align-middle shadow-xl transition-all p-5">
							<div className="flex flex-col gap-5">
								<div className="flex items-center justify-between">
									<Dialog.Title className="text-primary font-medium font-primary text-xl">
											{isTurningOn ? "Turn on campaign" : "Turn off campaign"}
									</Dialog.Title>
									<IconButton Icon={ExitIcon} className="-mr-2.5 hover:bg-primary/20" iconClassName="fill-primary" onClick={closeModal} />
								</div>

								<div className="flex flex-col gap-4 font-primary text-sm text-primary">
									{isTurningOn ? (
										<>
											<p>Meco will drive new subscribers to your newsletter. New subscribers can start joining through this campaign immediately.</p>
											<div className="flex gap-3 rounded-xl border border-primary-100 bg-primary/5 p-3">
												<div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-blue-500/10 text-blue-500">
													<InformationCircleIcon className="h-5 w-5" />
												</div>
												<p>Your monthly budget still applies. If you’ve already reached it this month, increase your budget first to keep the campaign running.</p>
											</div>
										</>
									) : (
										<>
											<p>Your newsletter will stop being recommended to new Meco readers. This campaign will stay off until you turn it back on and will not restart automatically next month.</p>
											{isAffiliateEngaged && (
												<div className="flex gap-3 rounded-xl border border-amber-500/30 bg-amber-500/10 p-3">
													<div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-amber-500/15 text-amber-500">
														<ExclamationTriangleIcon className="h-5 w-5" />
													</div>
													<p>
														People already in screening will continue through their {engagedWindow}-day screening period. If they qualify as engaged, they will still be added to your newsletter and charged under this campaign.
													</p>
												</div>
											)}
										</>
									)}
								</div>

								<div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
									<button type="button" disabled={isLoading} onClick={closeModal} className="h-[44px] rounded-xl border border-primary-200 px-5 font-primary text-base text-primary transition hover:bg-primary/10 disabled:opacity-50">
										Cancel
									</button>
									<button
										type="button"
										disabled={isLoading}
										onClick={changeCampaignStatus}
										className={`h-[44px] min-w-[150px] rounded-xl px-5 font-primary text-base text-white transition disabled:opacity-50 ${isTurningOn ? "bg-success-green hover:opacity-90" : "bg-brand-red hover:opacity-90"}`}>
										{isLoading ? <LoadingSpinner className="m-auto h-[20px] text-white/20 animate-spin fill-white" /> : isTurningOn ? "Turn on campaign" : "Turn off campaign"}
									</button>
								</div>
							</div>
						</Dialog.Panel>
					</Transition.Child>
				</div>
			</Dialog>
		</Transition>
	);
};

export default CampaignStatusModal;
